import { generateUrl } from '@/domains/productDiscovery/algolia/components/AlgoliaProductCard/AlgoliaProductCard.utils';
import type {
  Category,
  RhinoProduct,
  RhinoProductBrand,
  RhinoSeller,
  RhinoSponsoredProduct,
} from '@/domains/productDiscovery/ProductPage/interfaces/RhinoProduct';
import type {
  RhinoBrand,
  RhinoPrice,
  RhinoRating,
} from '@/domains/productDiscovery/ProductPage/interfaces/RhinoResponse';
import type { MabayaProducts } from '@/domains/productDiscovery/SponsoredProductRecommendation/interfaces/MabayaProduct';
import { getTags } from '@/productDiscovery/CommercialAnimation/utils/getProductPromoTags';

export const processRhinoBrand = (brand: RhinoBrand) => ({
  id: brand.id,
  name: brand.name,
  description: brand.description,
  imageUrl: brand.image_url,
});

export const processRhinoRating = (rating: RhinoRating) => ({
  average: rating.average,
  totalRatings: rating.total_ratings,
});

export const processRhinoPrice = (price: RhinoPrice) => ({
  currentPrice: {
    amountWithVat: price.current_price?.amount_with_vat,
    amountWithoutVat: price.current_price?.amount_without_vat,
  },
  retailPrice: {
    amountWithVat: price.retail_price?.amount_with_vat,
    amountWithoutVat: price.retail_price?.amount_without_vat,
  },
});

export const processRhinoCategory = (category: Category | undefined) => {
  if (!category) {
    return {};
  }

  if (!category.ancestors) {
    return {
      categoriesLast: category.name,
      categoriesLastId: category.id,
    };
  }

  const categoriesL0 = category.ancestors.find((a) => a.level === 0);
  const categoriesL1 = category.ancestors.find((a) => a.level === 1);
  const categoriesL2 = category.ancestors.find((a) => a.level === 2);
  const categoriesL3 = category.ancestors.find((a) => a.level === 3);

  return {
    categoriesLast: category.name,
    categoriesLastId: category.id,
    categoriesL0:
      categoriesL0?.name || (category.level === 0 ? category.name : undefined),
    categoriesL0Id:
      categoriesL0?.id || (category.level === 0 ? category.id : undefined),
    categoriesL1:
      categoriesL1?.name || (category.level === 1 ? category.name : undefined),
    categoriesL1Id:
      categoriesL1?.id || (category.level === 1 ? category.id : undefined),
    categoriesL2:
      categoriesL2?.name || (category.level === 2 ? category.name : undefined),
    categoriesL2Id:
      categoriesL2?.id || (category.level === 2 ? category.id : undefined),
    categoriesL3:
      categoriesL3?.name || (category.level === 3 ? category.name : undefined),
    categoriesL3Id:
      categoriesL3?.id || (category.level === 3 ? category.id : undefined),
  };
};

export const getAdvertiserAndSponsor = (
  brand: RhinoProductBrand,
  seller: RhinoSeller,
  adPayer?: 'BRAND' | 'SELLER',
) => {
  if (adPayer === 'SELLER') {
    return {
      advertiser: seller.name,
      sponsor: seller.name,
    };
  }

  if (adPayer === 'BRAND') {
    return {
      advertiser: seller.name,
      sponsor: brand.name,
    };
  }
  return { advertiser: '', sponsor: '' };
};

export const rhinoSponsoredProductMapper = (
  product: RhinoProduct,
  mabayaProducts: MabayaProducts[],
): RhinoSponsoredProduct => {
  const mabayaProduct = mabayaProducts.find(
    (p: MabayaProducts) => p.productId === product.productId,
  );

  return {
    ...product,
    adTrackingData: mabayaProduct?.adTrackingData || '',
    cpc: mabayaProduct?.cpc || 0,
    hasPromoLabel:
      getTags(
        product.price.currentPrice?.amountWithVat ?? 0,
        product.price.retailPrice?.amountWithVat ?? 0,
        product.promoTags,
      ).length > 0,
    url: generateUrl(
      {
        url: product.productSlug,
        modelId: product.modelId,
        articleId: product.articleId,
        modelsCount: product.modelsCount,
        productId: product.productId?.toString(),
      },
      false,
      true,
    ),
    adPayer: mabayaProduct?.adPayer,
    ...getAdvertiserAndSponsor(
      product.brand,
      product.seller,
      mabayaProduct?.adPayer,
    ),
    isTopSale: false,
    ...processRhinoCategory(product.category),
  };
};

export const processRhinoSponsoredProduct = (
  result: RhinoProduct[],
  mabayaProducts: MabayaProducts[],
): RhinoSponsoredProduct[] =>
  result.map((product) => rhinoSponsoredProductMapper(product, mabayaProducts));
